import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { toast } from "sonner";

// Import the new CheckMate Dashboard
import { CheckMateDashboard } from "./checkmate/CheckMateDashboard";


/**
 * SoraAssistant - Main CheckMate component
 * 
 * This component handles:
 * - Loading SORA optimization data from the API
 * - Passing data to the CheckMateDashboard for display
 * - Error handling and loading states
 * 
 * The actual UI is rendered by CheckMateDashboard (two-panel layout)
 */
export const SoraAssistant = ({ missionId, mission, aircraft, onMissionUpdate, onNavigateToProvisionalTarget, isProvisionalTargetActionable }) => {
  const { language } = useLanguage();
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [optimizationData, setOptimizationData] = useState(null);
  const [error, setError] = useState(null);
  
  // Fetch SORA optimization data
  const fetchOptimizations = useCallback(async () => {
    if (!missionId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/sora/optimization/${missionId}`);
      setOptimizationData(response.data);
    } catch (err) {
      console.error("Failed to fetch optimization data:", err);
      setError(err.response?.data?.detail || (language === "cs" ? "Nepodařilo se načíst data" : "Failed to load data"));
      toast.error(language === "cs" ? "Chyba při načítání dat" : "Error loading data");
    } finally {
      setLoading(false);
    }
  }, [missionId, language]);
  
  // Load data on mount
  useEffect(() => {
    fetchOptimizations();
  }, [fetchOptimizations]);
  
  // Loading state
  if (loading) {
    return (
      <Card className="border-[var(--card-border)] bg-[var(--card-bg)]">
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-3 py-12">
            <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
            <span className="text-[var(--text-secondary)]">
              {language === "cs" ? "Načítám SORA analýzu..." : "Loading SORA analysis..."}
            </span>
          </div>
          <div className="space-y-3 mt-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Card className="border-[var(--card-border)] bg-[var(--card-bg)]">
        <CardContent className="p-6">
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={fetchOptimizations}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              {language === "cs" ? "Zkusit znovu" : "Try again"}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // No data state
  if (!optimizationData) {
    return (
      <Card className="border-[var(--card-border)] bg-[var(--card-bg)]">
        <CardContent className="p-6">
          <div className="text-center py-12 text-[var(--text-muted)]">
            {language === "cs" ? "Žádná data k zobrazení" : "No data to display"}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Render the CheckMate Dashboard
  return (
    <CheckMateDashboard
      missionId={missionId}
      mission={mission}
      aircraft={aircraft}
      optimizationData={optimizationData}
      language={language}
      onMissionUpdate={onMissionUpdate}
      onRefreshOptimizations={fetchOptimizations}
      onNavigateToProvisionalTarget={onNavigateToProvisionalTarget}
      isProvisionalTargetActionable={isProvisionalTargetActionable}
    />
  );
};

export default SoraAssistant;
